import type { MeResponse } from '@exam-prep/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, get, post, setUnauthorizedHandler } from './api';

export type Me = MeResponse;

export { hasPermission } from './permissions';

interface StepUpRequest {
  resolve: () => void;
  reject: (e: unknown) => void;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** A signed-in session stopped working (401): show the 401 screen, not an empty page. */
  expired: boolean;
  clearExpired: () => void;
  setMe: (me: Me | null) => void;
  logout: () => Promise<void>;
  /** Run a mutation; if the API asks for a fresh TOTP, prompt, verify and retry once. */
  withStepUp: <T>(fn: () => Promise<T>) => Promise<T>;
  stepUp: StepUpRequest | null;
  finishStepUp: (ok: boolean, error?: unknown) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await get<Me>('/admin/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null);
  const [expired, setExpired] = useState(false);
  const clearExpired = useCallback(() => {
    setExpired(false);
  }, []);

  const setMe = useCallback(
    (me: Me | null) => {
      qc.setQueryData(['me'], me);
      if (me) setExpired(false);
      if (!me) qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    },
    [qc],
  );

  // A 401 on any signed-in call means the session is gone (expired, revoked, or 2FA lapsed):
  // forget it and show the 401 screen (sign in again / go home).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!qc.getQueryData(['me'])) return;
      setExpired(true);
      setMe(null);
    });
    return () => setUnauthorizedHandler(null);
  }, [qc, setMe]);

  const logout = useCallback(async () => {
    try {
      await post('/admin/auth/logout');
    } finally {
      setMe(null);
    }
  }, [setMe]);

  const withStepUp = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'auth.step_up_required')) throw e;
      await new Promise<void>((resolve, reject) => {
        setStepUp({ resolve, reject });
      });
      return fn();
    }
  }, []);

  const finishStepUp = useCallback(
    (ok: boolean, error?: unknown) => {
      if (!stepUp) return;
      if (ok) stepUp.resolve();
      else stepUp.reject(error ?? new Error('Cancelled'));
      setStepUp(null);
    },
    [stepUp],
  );

  const value = useMemo<AuthState>(
    () => ({
      me: meQuery.data ?? null,
      loading: meQuery.isPending,
      expired,
      clearExpired,
      setMe,
      logout,
      withStepUp,
      stepUp,
      finishStepUp,
    }),
    [
      meQuery.data,
      meQuery.isPending,
      expired,
      clearExpired,
      setMe,
      logout,
      withStepUp,
      stepUp,
      finishStepUp,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
